import React, { useRef, useState } from "react";
import { useHistory, useParams } from "react-router";
import { SubmitHandler, FormHandles } from "@unform/core";
import { Form } from "@unform/web";
import {
  IonButton,
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCol,
  IonGrid,
  IonLabel,
  IonRow,
  IonSelectOption,
  IonText,
  useIonViewDidEnter,
  useIonViewWillLeave,
} from "@ionic/react";
import { format } from "date-fns";

import { useToast } from "../../hooks/toast";
import { useValidationErrors } from "../../hooks/errors";
import api from "../../services/api";
import assistsIntercurrencesSchema from "../../schemas/Legacy/assists_intercurrences_schema";

import Main from "../../components/Main";
import TimeInput from "../../components/Legacy/UnForm/TimeInput";
import Select from "../../components/Legacy/UnForm/Select";
import Textarea from "../../components/Legacy/UnForm/Textarea";

import { getIntercurrences } from "../../shared/Intercurrences";

interface ParamTypes {
  assistanceId: string;
  patientName: string;
}

interface AssistsIntercurrence {
  id: number;
  assistance_id: number;
  time: string;
  intercurrence: string;
  note: string;
  created_at: string;
  updated_at: string;
}

const TechnicianAssistsIntercurrences: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const { addToast, successToast } = useToast();
  const { getValidationErrors } = useValidationErrors();
  const { assistanceId, patientName } = useParams<ParamTypes>();
  const history = useHistory();

  const [scroll, setScroll] = useState(new Date());
  const [id, setId] = useState<number>(0);
  const [data, setData] = useState<AssistsIntercurrence[]>([]);

  const getList = (assistanceId: string) => {
    api
      .get<any>(`technician/assists_intercurrences/${assistanceId}`)
      .then((response) => {
        setData(response.data);
      });
  };

  const timeToNow = () => {
    let currentTime = format(new Date(), "HH:mm");
    formRef?.current?.setFieldValue("time", currentTime);
  };

  useIonViewDidEnter(() => {
    formRef?.current?.reset();
    setData([]);
    timeToNow();
    setId(0);
    setScroll(new Date());
    getList(assistanceId);
  }, [assistanceId]);

  useIonViewWillLeave(() => {
    formRef?.current?.reset();
    setData([]);
    setId(0);
  }, [assistanceId]);

  const handleSubmit: SubmitHandler = async (data) => {
    assistsIntercurrencesSchema
      .validate(data)
      .then(async () => {
        if (id > 0) {
          update(data);
        } else {
          create(data);
        }
      })
      .catch((error) => {
        getValidationErrors(error);
      });
  };

  const create = async (data: AssistsIntercurrence) => {
    await api
      .post(`technician/assists_intercurrences/${assistanceId}`, data)
      .then((_response) => {
        successToast();
        handleReset();
        getList(assistanceId);
      })
      .catch((error) => {
        getValidationErrors(error.response);
      });
  };

  const update = async (data: AssistsIntercurrence) => {
    await api
      .put(`technician/assists_intercurrences/${assistanceId}/${id}`, data)
      .then((_response) => {
        successToast();
        handleReset();
        getList(assistanceId);
      })
      .catch((error) => {
        getValidationErrors(error.response);
      });
  };

  const handleReset = () => {
    formRef?.current?.reset();
    timeToNow();
    setId(0);
    setScroll(new Date());
  };

  const setFormValues = (values: AssistsIntercurrence) => {
    if (!!values.id) {
      setId(values.id);
      formRef?.current?.setData({ ...values });
      setScroll(new Date());
    }
  };

  return (
    <Main title="Intercorrências" scroll={scroll} back>
      <IonCard>
        <IonCardHeader>
          <IonRow>
            <IonCol size="6">
              <IonButton
                type="button"
                color="success"
                expand="block"
                onClick={() => handleReset()}
              >
                Novo
              </IonButton>
            </IonCol>
          </IonRow>
        </IonCardHeader>
        <IonCardContent>
          <IonGrid>
            <Form ref={formRef} onSubmit={handleSubmit}>
              <IonRow className="ion-align-items-end">
                <TimeInput size="6" name="time" label="Hora" />
                <Select size="12" name="intercurrence" label="Intercorrência">
                  {getIntercurrences.map((values, index) => (
                    <IonSelectOption key={index} value={values.value}>
                      {values.label}
                    </IonSelectOption>
                  ))}
                </Select>
                <Textarea label="Observação" name="note" rows={6} size="12" />
              </IonRow>
              <IonRow className="ion-margin-top">
                <IonCol size="12">
                  <IonLabel>Paciente: </IonLabel>
                  <IonText>{patientName}</IonText>
                </IonCol>
                <IonCol size="6" className="ion-margin-top">
                  <IonButton type="submit" color="primary" expand="block">
                    SALVAR
                  </IonButton>
                </IonCol>
                <IonCol size="6" className="ion-margin-top">
                  <IonButton
                    type="button"
                    onClick={() => history.goBack()}
                    color="medium"
                    expand="block"
                  >
                    Voltar
                  </IonButton>
                </IonCol>
              </IonRow>
            </Form>
          </IonGrid>
        </IonCardContent>
      </IonCard>
      {data.map((values) => (
        <IonCard key={values.id}>
          <IonCardContent>
            <IonRow>
              <IonCol size="12">
                <IonLabel>Hora: </IonLabel>
                <IonText>{values.time?.substr(0, 5)}</IonText>
              </IonCol>
              <IonCol size="12">
                <IonLabel>Intercorrência: </IonLabel>
                <IonText>{values.intercurrence}</IonText>
              </IonCol>
              {!!values.note && (
                <IonCol size="12">
                  <IonLabel>Observação: </IonLabel>
                  <IonText>{values.note}</IonText>
                </IonCol>
              )}
            </IonRow>
            <IonRow className="ion-margin-top">
              <IonCol size="6">
                <IonButton
                  type="button"
                  color="primary"
                  expand="block"
                  onClick={() => setFormValues(values)}
                >
                  Editar
                </IonButton>
              </IonCol>
            </IonRow>
          </IonCardContent>
        </IonCard>
      ))}
    </Main>
  );
};

export default TechnicianAssistsIntercurrences;
